import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Col, Row, Tag, Tooltip } from 'antd';
import { formatDate, downloadFile } from '@/utils';
import { SAMIN_KEY } from '@/services/http';
import { AppButton, AttachmentsIcon, Flex } from '../common';
import { statusColor, filterIssueAttachments, isSamadUser } from './helpdeskUtils';

export default function HelpdeskIssueDesktop({
  issue,
  styles,
  addNoteModal,
  closeIssueModal,
  cancelIssueModal,
}) {

  const [t] = useTranslation(['common', 'helpdesk']);

  return (
    <>
      <Row className={styles.rowContainer}>
        <Col className="h-full" span={5}>
          <Flex gap={0} direction="column">
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>{t('common:labels.id')}</span>
              <span className={styles.infoVal}>{issue?.id}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>{t('helpdesk:labels.request-date')}</span>
              <span className={styles.infoVal}>{formatDate(issue?.created_on, 'jYYYY/jMM/jDD')}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>{t('common:labels.id')}</span>
              <span className={styles.infoVal}>{issue?.project?.name}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>{t('common:labels.category')}</span>
              <span className={styles.infoVal}>{issue?.category?.name}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>{t('common:labels.status')}</span>
              <span className={styles.infoVal}>
                <Tag
                  color={statusColor[issue?.status?.name]}
                  className="!rounded-full !text-sm !px-2.5 !py-1 font-medium text-white"
                >{issue?.status?.name}</Tag>
              </span>
            </div>
          </Flex>
        </Col>
        <Col span={19}>
          <Row className="h-full">
            <Col className="h-full" flex="1">
              <Flex direction="column" className={styles.box.base}>
                <div className={styles.box.title}>{issue?.subject}</div>
                <div className="text-mid">{issue?.description}</div>
                <Flex gap={12} className="ms-auto mt-auto" inlineFlex>
                  {filterIssueAttachments(issue)?.map(att => (
                    <Tooltip key={att.id} title={t('helpdesk:labels.download-attachment')}>
                      <div
                        className="p-0 m-0 cursor-pointer"
                        onClick={() => downloadFile(att.content_url + '?key=' + SAMIN_KEY, att.filename)}>
                        <AttachmentsIcon style={{ fontSize: '2rem' }}
                        />
                      </div>
                    </Tooltip>
                  ))}
                </Flex>
              </Flex>
            </Col>
            <Col className="h-full mt-auto" flex={styles.leftSideFlex}>
              <Flex justify="flex-end" gap={12} direction="column" className="h-full px-4">
                <AppButton
                  ghost
                  type="primary"
                  onClick={addNoteModal.show}
                  className="!font-medium !text-app-blue !border-app-blue">{t('helpdesk:labels.new-reply')}</AppButton>
                {issue?.author?.id === issue?.assigned_to?.id ?
                  <AppButton onClick={closeIssueModal.show} appVariant="cancel-gray" ghost>
                    {t('helpdesk:labels.close-request')}
                  </AppButton> :
                  <AppButton onClick={cancelIssueModal.show} ghost danger className="!font-medium">
                    {t('helpdesk:labels.cancel-request')}
                  </AppButton>
                }
              </Flex>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={5}></Col>
        <Col span={19}>
          <Row>
            <Col flex="1">
              {issue?.journals?.map((journal, i) => {
                if (journal?.notes?.trim() === '') return '';
                const details = journal?.details.filter(x => x.property === 'attachment');
                const journalAttachments = issue?.attachments?.filter(att => details.some(ja => ja.name === String(att.id)));
                return (
                  <Flex
                    key={i}
                    className="p-2 mb-4 flex-col !border-helpdesk"
                    style={issue.journals.length - 1 === i ? {} : { borderBottom: '1px dashed' }}
                  >
                    <Flex align="center" gap={12} className="text-helpdesk">
                      <div className={
                        classNames(isSamadUser(journal.user) ?
                          'bg-white' :
                          'bg-helpdesk',
                        styles.circle
                        )
                      } />
                      <span>{journal.user?.name}</span>
                      <Flex align="center" className="ms-auto">
                        {journalAttachments.map((att, j) =>
                          <Tooltip key={j} title={t('helpdesk:labels.download-attachment')}>
                            <div
                              className="p-0 m-0 cursor-pointer"
                              onClick={() => downloadFile(att.content_url + '?key=' + SAMIN_KEY, att.filename)}
                            >
                              <AttachmentsIcon style={{ fontSize: '2rem' }} />
                            </div>
                          </Tooltip>
                        )}
                        <span>{formatDate(journal.created_on, 'jYYYY/jMM/jDD')}</span>
                      </Flex>
                    </Flex>
                    <p className="text-justify text-app-gray-darker mb-2">
                      {journal.notes}
                    </p>
                  </Flex>
                );
              })}
            </Col>
            <Col flex={styles.leftSideFlex}></Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}
